import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  User, Mail, Calendar, CreditCard, ChevronDown, ChevronUp, 
  CheckCircle, XCircle, Clock, AlertCircle, ExternalLink, Loader2, MessageSquare 
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import CancellationFeedbackModal from '@/components/CancellationFeedbackModal';
import FeedbackBanner from '@/components/FeedbackBanner';
import { logActivity } from '@/lib/activityLog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CANCELLATION_REASONS = [
  { id: 'too_expensive', label: 'Too expensive' },
  { id: 'not_using', label: 'Not using it enough' },
  { id: 'found_alternative', label: 'Found a better alternative' },
  { id: 'missing_features', label: 'Missing features I need' },
  { id: 'technical_issues', label: 'Technical issues' },
  { id: 'other', label: 'Other' }
];

export default function Profile({ user, onLogout }) {
  const [subscription, setSubscription] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPayments, setShowPayments] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);
  
  // Feedback state
  const [hasCancelled, setHasCancelled] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [feedbackText, setFeedbackText] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  useEffect(() => {
    logActivity('profile_viewed');
    fetchProfileData();
    checkIfCancelled();
  }, []);

  const checkIfCancelled = async () => {
    try {
      const response = await axios.get(`${API}/feedback/reminder-status`);
      // User has cancelled if there's a cancellation record
      const cancelled = response.data.has_feedback !== undefined || 
                       response.data.show_reminder || 
                       response.data.max_reached || 
                       response.data.recently_dismissed;
      setHasCancelled(cancelled);
    } catch (error) {
      console.log('Feedback check skipped');
      setHasCancelled(false);
    }
  };

  const fetchProfileData = async () => {
    try {
      const [subRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/subscription/status`).catch(() => ({ data: null })),
        axios.get(`${API}/payments/history`).catch(() => ({ data: [] }))
      ]);

      setSubscription(subRes.data);
      setPayments(paymentsRes.data || []);
    } catch (error) {
      console.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async (feedback) => {
    setCancelling(true);
    try {
      await axios.post(`${API}/subscription/cancel`, {
        feedback: feedback || null
      });
      toast.success('Subscription cancelled. You\'ll have access until the end of your billing period.');
      setShowCancelModal(false);
      
      // Force refresh subscription data
      await fetchProfileData();
    } catch (error) {
      console.error('Cancellation error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to cancel subscription';
      toast.error(errorMsg);
      throw error;
    } finally {
      setCancelling(false);
    }
  };

  const handleManageBilling = async () => {
    setLoadingPortal(true);
    try {
      const response = await axios.post(`${API}/subscription/billing-portal`);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open billing portal');
      setLoadingPortal(false);
    }
  };

  const handleReasonToggle = (reasonId) => {
    setSelectedReasons(prev => 
      prev.includes(reasonId) 
        ? prev.filter(id => id !== reasonId)
        : [...prev, reasonId]
    );
  };

  const handleSubmitFeedback = async () => {
    if (selectedReasons.length === 0 && !feedbackText.trim()) {
      toast.error('Please select at least one reason or provide feedback');
      return;
    }

    setSubmittingFeedback(true);
    try {
      await axios.post(`${API}/feedback/submit-authenticated`, {
        reasons: selectedReasons,
        additional_feedback: feedbackText.trim() || null
      });
      
      toast.success('Thank you for your feedback!');
      setShowFeedbackForm(false);
      setSelectedReasons([]);
      setFeedbackText('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-GB', { 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return 'N/A';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'succeeded':
      case 'paid':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'succeeded':
      case 'paid':
        return 'text-green-600 bg-green-50';
      case 'failed':
        return 'text-red-600 bg-red-50';
      case 'pending':
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar user={user} onLogout={onLogout} />
      
      {/* Feedback Reminder Banner */}
      <FeedbackBanner />
      
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">Profile</h1>
          <p className="text-gray-500 mt-1">Manage your account and subscription</p>
        </div>

        {/* Profile Card */}
        <Card className="mb-6 overflow-hidden border-0 shadow-sm">
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-6 py-8">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <User className="h-8 w-8 text-white" />
              </div>
              <div className="text-white">
                <h2 className="text-xl font-semibold">
                  {user.first_name} {user.last_name}
                </h2>
                <p className="text-blue-100 flex items-center gap-2 mt-1">
                  <Mail className="h-4 w-4" />
                  {user.email}
                </p>
              </div>
            </div>
          </div>
          
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-gray-600">
              <Calendar className="h-5 w-5 text-gray-400" />
              <span>Member since {formatDate(user.created_at)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Card */}
        <Card className="mb-6 border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Subscription</h3>
                  <p className="text-sm text-gray-500">
                    {subscription?.has_subscription ? subscription.plan || 'Active' : 'No active subscription'}
                  </p>
                </div>
              </div>
              {subscription?.has_subscription && (
                <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${
                  subscription.cancel_at_period_end ? 'bg-orange-100 text-orange-700' :
                  subscription.status === 'active' ? 'bg-green-100 text-green-700' :
                  subscription.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                  subscription.status === 'trialing' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {subscription.cancel_at_period_end ? 'Cancelled' : 
                   subscription.status === 'trialing' ? 'Trial' : 
                   subscription.status}
                </span>
              )}
            </div>

            {subscription?.has_subscription && (
              <div className="space-y-4">
                {/* Billing Info */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Plan</span>
                    <span className="font-medium text-gray-900">£27/month</span>
                  </div>
                  {subscription.renews_at && subscription.status === 'active' && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Next invoice</span>
                      <span className="font-medium text-gray-900">{formatDate(subscription.renews_at)}</span>
                    </div>
                  )}
                  {subscription.ends_at && subscription.cancel_at_period_end && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Access until</span>
                      <span className="font-medium text-orange-600">{formatDate(subscription.ends_at)}</span>
                    </div>
                  )}
                  {subscription.started_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Started</span>
                      <span className="font-medium text-gray-900">{formatDate(subscription.started_at)}</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={handleManageBilling}
                    disabled={loadingPortal}
                    className="flex-1"
                  >
                    {loadingPortal ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <ExternalLink className="h-4 w-4 mr-2" />
                    )}
                    Manage Billing
                  </Button>
                  
                  {subscription.status === 'active' && !subscription.cancel_at_period_end && (
                    <Button
                      variant="ghost"
                      onClick={() => setShowCancelModal(true)}
                      className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      Cancel Subscription
                    </Button>
                  )}
                </div>

                {/* Cancellation Feedback Modal */}
                <CancellationFeedbackModal
                  open={showCancelModal}
                  onClose={() => setShowCancelModal(false)}
                  onConfirm={handleCancelSubscription}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment History */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-0">
            <button
              onClick={() => setShowPayments(!showPayments)}
              className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-gray-600" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">Payment History</h3>
                  <p className="text-sm text-gray-500">
                    {payments.length} transaction{payments.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              {showPayments ? (
                <ChevronUp className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              )}
            </button>

            {showPayments && (
              <div className="border-t border-gray-100">
                {payments.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    No payment history yet
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {payments.map((payment, index) => (
                      <div key={payment.id || index} className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {getStatusIcon(payment.status)}
                          <div>
                            <p className="font-medium text-gray-900">
                              £{payment.amount?.toFixed(2) || '0.00'}
                            </p>
                            <p className="text-sm text-gray-500">
                              {formatDate(payment.created_at)}
                            </p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(payment.status)}`}>
                          {payment.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* General Feedback Section - Always visible for all users */}
        <Card id="feedback" className="scroll-mt-20">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold">Share Your Thoughts</h3>
              </div>
              {!showFeedbackForm && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFeedbackForm(true)}
                  className="border-blue-200 text-blue-600 hover:bg-blue-50"
                >
                  Give Feedback
                </Button>
              )}
            </div>

            {showFeedbackForm ? (
              <div className="space-y-4 animate-in fade-in duration-300">
                <p className="text-sm text-gray-600">
                  Your feedback helps me improve MealGuide. I read every message personally! - Knut
                </p>

                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    What can I improve? (optional)
                  </Label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="feedback-pricing"
                        checked={selectedReasons.includes('too_expensive')}
                        onCheckedChange={() => handleReasonToggle('too_expensive')}
                      />
                      <label htmlFor="feedback-pricing" className="text-sm cursor-pointer">
                        Pricing
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="feedback-features"
                        checked={selectedReasons.includes('missing_features')}
                        onCheckedChange={() => handleReasonToggle('missing_features')}
                      />
                      <label htmlFor="feedback-features" className="text-sm cursor-pointer">
                        Missing features
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="feedback-ux"
                        checked={selectedReasons.includes('not_using')}
                        onCheckedChange={() => handleReasonToggle('not_using')}
                      />
                      <label htmlFor="feedback-ux" className="text-sm cursor-pointer">
                        User experience
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="feedback-tech"
                        checked={selectedReasons.includes('technical_issues')}
                        onCheckedChange={() => handleReasonToggle('technical_issues')}
                      />
                      <label htmlFor="feedback-tech" className="text-sm cursor-pointer">
                        Technical issues
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="feedback-other"
                        checked={selectedReasons.includes('other')}
                        onCheckedChange={() => handleReasonToggle('other')}
                      />
                      <label htmlFor="feedback-other" className="text-sm cursor-pointer">
                        Other
                      </label>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="feedback-text" className="text-sm font-medium mb-2 block">
                    Your thoughts
                  </Label>
                  <Input
                    id="feedback-text"
                    placeholder="Share your ideas, suggestions, or concerns..."
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleSubmitFeedback}
                    disabled={submittingFeedback}
                    className="flex-1 bg-[#3477f8] hover:bg-[#2563eb]"
                  >
                    {submittingFeedback ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Sending...</>
                    ) : (
                      'Send to Knut'
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowFeedbackForm(false);
                      setSelectedReasons([]);
                      setFeedbackText('');
                    }}
                    disabled={submittingFeedback}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Have ideas or feedback? I'd love to hear from you.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Sign Out Button */}
        <div className="mt-8 text-center">
          <Button
            variant="ghost"
            onClick={onLogout}
            className="text-gray-500 hover:text-gray-700"
          >
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
